import { seriesSvgBar, seriesSvgMulti, seriesSvgLine } from '@d3fc/d3fc-series';
import { select, selectAll } from 'd3-selection';
import { Series } from 'd3-shape';
import {
    LiquiditySummaryChartBar,
    LiquiditySummaryChartSeriesPoint,
} from './liquiditySummaryChartData';
import { LiquiditySummarySelection } from './selection';
import { annotationSvgGridline } from '@d3fc/d3fc-annotation';
import { BarMouseOver } from './BarMouseOver';
import { ScaleLinear } from 'd3-scale';
import constants from './variables.module.scss';

const TodayBarRow = 0;
const HistBarRow = 1;

const barWidth = parseInt(constants.barWidth);
const markerPadding = parseInt(constants.markerPadding);
const markerMargin = parseInt(constants.markerMargin);
const markerHeight = parseInt(constants.markerHeight);
const lineWidth = parseInt(constants.lineWidth);

const keyToSection = (key: string) => {
    switch (key) {
        case 'remainingVolume':
        case 'close':
            return 'section-close';
        case 'open':
        case 'volume':
        default:
            return 'section-open';
    }
};

export const liquiditySummaryChartSeries = (
    series: Series<LiquiditySummaryChartBar, string>[],
    barMouseOver: BarMouseOver,
    yScale: ScaleLinear<number, number>
) => {
    const barSeries = seriesSvgBar()
        .orient('horizontal')
        .align('left')
        .crossValue((d: LiquiditySummaryChartSeriesPoint) => d.data.row)
        .mainValue((d: LiquiditySummaryChartSeriesPoint) => d[1])
        .baseValue((d: LiquiditySummaryChartSeriesPoint) => d[0])
        .bandwidth(barWidth)
        .decorate((selection: LiquiditySummarySelection) => {
            selection.enter().each((data, index, nodes) => {
                const barSelection = select(nodes[index])
                    .classed('liquidity-summary-block', true)
                    .classed('today', data.data.row === TodayBarRow)
                    .classed('hist', data.data.row === HistBarRow)
                    .classed(data.key, true)
                    .classed(keyToSection(data.key), true);

                // Listen to mouse events for the legends...
                barSelection
                    .on('mouseenter', () => {
                        // barSelection.classed('selected', true);
                        const highlight = selectAll(
                            `.${keyToSection(data.key)}`
                        );
                        highlight.classed('selected', true);

                        barMouseOver.publish({
                            bar:
                                data.data.row === TodayBarRow
                                    ? 'today'
                                    : 'hist',
                            key: data.key,
                        });
                    })
                    .on('mouseleave', () => {
                        const highlight = selectAll(
                            `.${keyToSection(data.key)}`
                        );
                        highlight.classed('selected', false);
                        barMouseOver.publish({});
                    });
            });

            // Now/Close Section Dividers
            selection
                .enter()
                .filter((d) => d.key === 'volume' || d.key === 'close')
                .append('rect')
                .attr('x', (d) => {
                    return d.key === 'volume' ? -2 : -2;
                })
                .attr('y', barWidth / -2)
                .attr('width', lineWidth)
                .attr('height', barWidth)
                .attr('height', (d) => {
                    return d.data.row === TodayBarRow
                        ? barWidth + markerMargin
                        : barWidth;
                })
                .attr('class', 'marker-line');

            // Marker Rectangle
            selection
                .enter()
                .filter((d) => d.key === 'volume' || d.key === 'close')
                .filter((d) => d.data.row === TodayBarRow)
                .append('rect')
                .attr('y', barWidth / 2 + markerMargin)
                .attr('height', markerHeight)
                .attr('class', 'marker-rect');

            // Marker Text
            selection
                .enter()
                .filter((d) => d.key === 'volume' || d.key === 'close')
                .filter((d) => d.data.row === TodayBarRow)
                .append('text')
                .text((d) => {
                    return keyToSection(d.key) === 'section-open'
                        ? 'Now'
                        : 'Close';
                })
                .attr('y', barWidth + markerHeight / 2)
                .attr('align-baseline', 'middle')
                .attr('class', 'marker-text');

            const textLength = (
                selection
                    .select('text.marker-text')
                    .node() as SVGTextContentElement
            )?.getComputedTextLength();

            if (textLength) {
                selection
                    .select('rect.marker-rect')
                    .attr('x', textLength / -2 - markerPadding)
                    .attr('width', textLength + 2 * markerPadding);
                selection.select('text.marker-text').attr('x', textLength / -2);
            }
        });

    // Series for lines to join the bars...
    const lineSeries = seriesSvgLine()
        .orient('horizontal')
        .crossValue((d: LiquiditySummaryChartSeriesPoint) => {
            if (d.data.row === TodayBarRow) {
                // Move end of line up for today...
                return yScale.invert(yScale(0));
            } else {
                // Move end of line down for hist...
                const bar1Y = yScale(1);
                const bar1YAdj = bar1Y + barWidth;
                const bar1Adj = yScale.invert(bar1YAdj);
                return bar1Adj;
            }
        })
        .mainValue((d: LiquiditySummaryChartSeriesPoint) => d[1])
        .decorate((selection: LiquiditySummarySelection) => {
            selection.enter().each((data, index, nodes) => {
                select(nodes[index])
                    .classed('line-movement', true)
                    .classed(data.key, true)
                    .attr('transform', `translate(${lineWidth / -2},0)`);
            });
        });

    const stackedBarSeries = seriesSvgMulti()
        .mapping((data: number[], index: number) => data[index])
        .series(series.map(() => barSeries));

    const stackedLineSeries = seriesSvgMulti()
        .mapping((data: number[], index: number) => data[index])
        .series(series.map(() => lineSeries));

    return seriesSvgMulti().series([
        annotationSvgGridline(),
        stackedBarSeries,
        stackedLineSeries,
    ]);
};
